import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface BackToTopProps {
  /** Pixels scrolled before the pill appears. Default 600. */
  showAfter?: number;
  className?: string;
}

/**
 * Floating "back to top" pill. Appears after scrolling past `showAfter`.
 * Positioned above the mobile bottom nav (safe-area aware).
 */
export function BackToTop({ showAfter = 600, className }: BackToTopProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > showAfter);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [showAfter]);

  const scrollTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <button
      type="button"
      onClick={scrollTop}
      aria-label="Back to top"
      style={{
        bottom: "calc(5.5rem + env(safe-area-inset-bottom))",
        touchAction: "manipulation",
      }}
      className={cn(
        "fixed right-4 z-40 md:bottom-6 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground shadow-lg px-3.5 py-2 text-sm font-medium transition-all duration-200",
        visible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-2 pointer-events-none",
        className
      )}
    >
      <ArrowUp className="h-4 w-4" />
      <span className="hidden sm:inline">Top</span>
    </button>
  );
}
